"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/lang";
import type { Hotel, SectionIds } from "@/data/home";

type HeroSectionProps = {
	sectionIds: SectionIds;
	hotels: Hotel[];
};

export function HeroSection({
	sectionIds: _sectionIds,
	hotels: _hotels,
}: HeroSectionProps) {
	const t = useLang();

	return (
		<section className="relative overflow-hidden h-dvh min-h-[560px] flex items-end bg-primary -mt-[5.5rem] sm:-mt-[6.5rem]">
			{/* Background video */}
			<div className="absolute inset-0">
				<video
					autoPlay
					muted
					loop
					playsInline
					className="absolute w-full h-full object-cover"
				>
					<source src="/hero-video.webm" type="video/webm" />
					<source src="/hero-video.mp4" type="video/mp4" />
				</video>
				{/* Gradient: strong at bottom for text, subtle at top */}
				<div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/30 to-primary/10" />
			</div>

			{/* Content — bottom-left anchored */}
			<div className="relative z-10 w-full pb-12 sm:pb-16 md:pb-20 px-4 sm:px-6 lg:px-8">
				<div className="mx-auto max-w-7xl">
					<div className="grid lg:grid-cols-[1fr_auto] lg:items-end gap-8 lg:gap-20">
						{/* Left: headline */}
						<div>
							{/* Label */}
							<motion.div
								initial={{ opacity: 0, x: -16 }}
								animate={{ opacity: 1, x: 0 }}
								transition={{ duration: 0.7, ease: "easeOut", delay: 0.2 }}
								className="flex items-center gap-3 mb-5 sm:mb-6"
							>
								<span className="h-px w-8 bg-accent/70" />
								<span className="text-label text-accent/90">
									{t.hero.label}
								</span>
							</motion.div>

							{/* H1 */}
							<motion.h1
								initial={{ opacity: 0, y: 24 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{
									duration: 0.9,
									ease: [0.16, 1, 0.3, 1],
									delay: 0.35,
								}}
								className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-normal text-white leading-[1.06] tracking-tight"
							>
								{t.hero.headline1}
								<span className="block mt-1 font-semibold text-white/70">
									{t.hero.headline2}
								</span>
							</motion.h1>

							{/* Gold line */}
							<motion.span
								initial={{ scaleX: 0 }}
								animate={{ scaleX: 1 }}
								transition={{ duration: 0.8, ease: "easeOut", delay: 1.0 }}
								className="block mt-6 sm:mt-8 h-px w-20 sm:w-28 bg-accent/60 origin-left"
							/>
						</div>

						{/* Right: two text blocks */}
						<div className="lg:max-w-sm lg:pb-1 hidden sm:flex flex-col gap-5">
							<motion.p
								initial={{ opacity: 0, y: 16 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.7, ease: "easeOut", delay: 0.6 }}
								className="text-base sm:text-lg font-normal text-white/80 leading-relaxed"
							>
								{t.hero.description1}
							</motion.p>

							<motion.div
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								transition={{ duration: 0.5, delay: 0.85 }}
								className="h-px w-full bg-white/10"
							/>

							<motion.p
								initial={{ opacity: 0, y: 12 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.7, ease: "easeOut", delay: 0.75 }}
								className="text-sm sm:text-base text-white/50 leading-relaxed"
							>
								{t.hero.description2}
							</motion.p>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
